import { cn } from '@neo/lib/utils'
import { Separator } from '@neo/primitives/separator'
import { Tooltip } from '@neo/primitives/tooltip'
import { getShortcutKeyString } from '@neo/shared/components/shortcut-key'
import { useKeyboardShortcuts } from '@shared/hooks/use-keyboard-shortcuts'
import { ControlButton, Controls, type Node } from '@xyflow/react'
import {
  Grid2x2Check,
  MessageCircleDashed,
  MousePointer2,
  PlusCircle,
  Redo,
  StickyNote,
  Undo,
} from 'lucide-react'
import { v4 } from 'uuid'
import { useFlowHistory } from '../hooks/use-flow-history'
import { useNodeLayout } from '../hooks/use-node-layout'
import { useTypedReactFlow } from '../hooks/use-react-flow'
import { useFlowRunStore } from '../run-info/store'
import { useFlowStore } from '../store'
import { type NoteNodeData, SYSTEM_PROMPT_NODE_ID } from '../types'
import { ZoomInOut } from './zoom-in-out'

const CONTROL_GROUP_CLASS = 'flex h-9 items-center rounded-md border border-st2 bg-background p-1'
const CONTROL_BUTTON_CLASS =
  '!size-7 !rounded-md !border-0 !bg-transparent !p-0 text-secondary hover:!bg-hv1 hover:text-primary focus-visible:!ring-2 focus-visible:!ring-brand focus-visible:!ring-offset-1 disabled:opacity-40'

export const FlowControls = ({ undo, redo }: { undo: () => void; redo: () => void }) => {
  const { layoutNodes } = useNodeLayout()
  const flowInstance = useFlowStore((state) => state.flowInstance)
  const { setNodes, setEdges } = useTypedReactFlow()
  const setCandidateNode = useFlowStore((state) => state.setCandidateNode)
  const isEditing = useFlowStore((state) => state.isEditing)
  const isSelectionModeActive = useFlowStore((state) => state.isSelectionModeActive)
  const toggleSelectionMode = useFlowStore((state) => state.toggleSelectionMode)
  const panelState = useFlowRunStore((state) => state.panelState)
  const selectedNodeID = useFlowStore((state) => state.selectedNodeID)
  const selectNode = useFlowStore((state) => state.selectNode)
  const systemPrompt = useFlowStore((state) => state.systemPrompt)
  const setSystemPrompt = useFlowStore((state) => state.setSystemPrompt)
  const setShowNodeSelectionModal = useFlowStore((state) => state.setShowNodeSelectionModal)

  const arrangeNodes = () => {
    const { nodes, edges } = layoutNodes()
    setNodes(nodes)
    setEdges(edges)
    setTimeout(() => {
      // Fit the view based on the number of nodes
      flowInstance?.fitView({ maxZoom: nodes?.length > 7 ? 0.8 : 1 })
    }, 10)
  }

  useKeyboardShortcuts([
    {
      shortcut: { key: 'A' },
      onlyOnHover: true,
      method: () => arrangeNodes(),
    },
  ])
  const undoDisabled = !isEditing || useFlowHistory.temporal.getState().pastStates.length === 0
  const redoDisabled = !isEditing || useFlowHistory.temporal.getState().futureStates.length === 0
  const hasExecutionHistory = useFlowRunStore((state) => state.executionHistory.length > 0)
  const showEvalSidebar = useFlowStore((state) => state.showEvalSidebar)

  const getControlsLeftPosition = () => {
    const isPanelExpanded = panelState !== 'minimized'

    if (showEvalSidebar) return isPanelExpanded ? 600 : 300
    if (hasExecutionHistory) return isPanelExpanded ? 400 : 300
    return 0
  }

  return (
    <Controls
      data-testid="flow-controls"
      showZoom={false}
      showFitView={false}
      showInteractive={false}
      className="flex !flex-row !gap-2 !bg-transparent !shadow-none transition-all duration-300"
      style={{
        left: `${getControlsLeftPosition()}px`,
      }}
    >
      <div className={CONTROL_GROUP_CLASS}>
        <ZoomInOut />
        <Separator orientation="vertical" className="mx-1 h-4" />
        <ControlButton
          disabled={undoDisabled}
          onClick={(e) => {
            e.stopPropagation()
            undo()
          }}
          data-testid="button-undo"
          className={CONTROL_BUTTON_CLASS}
        >
          <Tooltip
            content={`Undo (${getShortcutKeyString({ key: 'z', meta: true, shift: false })})`}
          >
            <Undo className="!size-4 !fill-none" />
          </Tooltip>
        </ControlButton>

        <ControlButton
          onClick={(e) => {
            e.stopPropagation()
            redo()
          }}
          data-testid="button-redo"
          disabled={redoDisabled}
          className={CONTROL_BUTTON_CLASS}
        >
          <Tooltip
            content={`Redo (${getShortcutKeyString({ key: 'y', meta: true, shift: false })})`}
          >
            <Redo className="!size-4 !fill-none" />
          </Tooltip>
        </ControlButton>
        <Separator orientation="vertical" className="mx-1 h-4" />
        <ControlButton
          onClick={arrangeNodes}
          data-testid="button-arrange"
          className={CONTROL_BUTTON_CLASS}
        >
          <Tooltip content="Arrange blocks (A)">
            <Grid2x2Check className="!size-4 !fill-none" />
          </Tooltip>
        </ControlButton>
      </div>
      <div className={CONTROL_GROUP_CLASS}>
        <ControlButton
          onClick={(e) => {
            e.stopPropagation()
            toggleSelectionMode()
          }}
          data-testid="button-toggle-selection-mode"
          aria-pressed={isSelectionModeActive}
          className={cn(CONTROL_BUTTON_CLASS, isSelectionModeActive && '!bg-hv1 !text-brand-text')}
        >
          <Tooltip
            content={isSelectionModeActive ? 'Disable Selection Mode' : 'Enable Selection Mode'}
          >
            <MousePointer2 className="!size-4 !fill-none" />
          </Tooltip>
        </ControlButton>
        <Separator orientation="vertical" className="mx-1 h-4" />
        <ControlButton
          className={CONTROL_BUTTON_CLASS}
          disabled={!isEditing}
          onClick={(e) => {
            if (!isEditing) return
            e.stopPropagation()
            setShowNodeSelectionModal(true)
          }}
          data-testid="button-add-node"
        >
          <Tooltip content={isEditing ? 'Add node' : 'Nodes can only be added in edit mode'}>
            <PlusCircle className="!size-4 !fill-none" />
          </Tooltip>
        </ControlButton>
        <ControlButton
          className={CONTROL_BUTTON_CLASS}
          disabled={!isEditing}
          onClick={(e) => {
            if (!isEditing) return
            const id = v4()
            e.stopPropagation()
            setCandidateNode({
              id,
              type: 'note',
              position: { x: 0, y: 0 },
              width: 250,
              height: 120,
              selectable: false,
              data: {
                meta: {
                  name: 'Note',
                },
                config: {
                  name: 'Note',
                  content: '',
                },
              },
            } as Node<NoteNodeData>)
          }}
          data-testid="button-add-note"
        >
          <Tooltip content={isEditing ? 'Add note' : 'Notes can only be added in edit mode'}>
            <StickyNote className="!size-4 !fill-none" />
          </Tooltip>
        </ControlButton>
        <Separator orientation="vertical" className="mx-1 h-4" />
        <ControlButton
          disabled={!isEditing}
          onClick={(e) => {
            if (!isEditing) return
            e.stopPropagation()
            if (!systemPrompt)
              setSystemPrompt({ prompt: '', ai_nodes: [], all_nodes_enabled: true })
            selectNode(SYSTEM_PROMPT_NODE_ID)
          }}
          data-testid="button-system-prompt"
          aria-pressed={selectedNodeID === SYSTEM_PROMPT_NODE_ID}
          className={cn(
            CONTROL_BUTTON_CLASS,
            '!h-7 !w-auto shrink-0 !px-2',
            selectedNodeID === SYSTEM_PROMPT_NODE_ID && '!bg-hv1 !text-brand-text',
          )}
        >
          <Tooltip
            content={isEditing ? 'Global Prompt' : 'Global Prompt can only be added in edit mode'}
          >
            <div className="flex items-center gap-1 whitespace-nowrap">
              <MessageCircleDashed className="!size-4 shrink-0 !fill-none" />
              <span className="font-inter text-s-500 normal-case tracking-normal">
                Global Prompt
              </span>
            </div>
          </Tooltip>
        </ControlButton>
      </div>
    </Controls>
  )
}
